import React from 'react';
import { Modal, Btn } from '../components';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { ProofOfPaymentNote } from '../components/ProofOfPaymentNote';
import { ImageLightbox } from '../components/ImageLightbox';
import { PayButton } from '../components/PayButton';
import { api, assetUrl } from '../lib/api';
import { formatInTz } from '../lib/timezone';
import { groupPaymentBlocks } from '../lib/paymentBlocks';
import { useViewerTz } from '../hooks/useViewerTz';
import { palette as T, type } from '../design';

// ropida/booking.jsx — Book seats on an instructor's scheduled sessions (MS Teams-style).
// Accepts one or many selected sessions: review them (times in the student's own tz) →
// request → per-session results + the instructor's payment instructions. Bookings stay
// "pending" until the instructor confirms the manual payment (GCash/bank) outside the app.

const font = "'Plus Jakarta Sans', sans-serif";

function BookingModal({ instructor, sessions, onClose, onConfirm }) {
  const list = React.useMemo(() => (Array.isArray(sessions) ? sessions : [sessions]).filter(Boolean), [sessions]);
  const [step, setStep] = React.useState('review'); // review | done
  const titleId = React.useId();
  const [studentTz, setStudentTz] = useViewerTz();
  const [notes, setNotes] = React.useState('');
  const [results, setResults] = React.useState([]); // [{ session, booking?, error? }]
  const [loading, setLoading] = React.useState(false);
  // PA-6: the cancellation rule comes from the server, so the pop-up never states a different
  // cutoff than the one enforced. Until it arrives (or if it cannot load) the rule is not shown;
  // booking still works, and the confirmation email states the rule too.
  const [freeCancelHours, setFreeCancelHours] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const policy = await api.get('/bookings/policy');
        if (live && Number.isFinite(policy?.freeCancelHours)) setFreeCancelHours(policy.freeCancelHours);
      } catch (err) {
        console.error('[BookingModal] loading the cancellation rule failed', err);
      }
    })();
    return () => { live = false; };
  }, []);

  // JTP-5: whether this student pays through Ropida's Pay, so the review step does not promise
  // the instructor's payment details. Until it arrives, or if it cannot load, today's words show;
  // the booking answer itself decides what the confirmation shows.
  const [payAvailable, setPayAvailable] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const mine = await api.get('/bookings/my');
        if (live) setPayAvailable(Boolean(mine?.payAvailable));
      } catch (err) {
        console.error('[BookingModal] loading whether Pay applies failed', err);
      }
    })();
    return () => { live = false; };
  }, []);

  const total = list.reduce((sum, s) => sum + (s.price || 0), 0);
  const many = list.length > 1;

  const handleConfirm = async () => {
    setLoading(true);
    // Book sequentially — each seat take is independent, and a full session must not
    // block the others. Results are reported per session.
    const out = [];
    for (const s of list) {
      try {
        const data = await api.post('/bookings', { sessionId: s.id, ...(notes.trim() ? { notes: notes.trim() } : {}) });
        out.push({ session: s, booking: data?.booking });
      } catch (err) {
        out.push({ session: s, error: err.data?.message || err.data?.error || err.message || 'Booking failed' });
      }
    }
    setResults(out);
    setLoading(false);
    setStep('done');
  };

  const booked = results.filter((r) => r.booking);
  const failed = results.filter((r) => r.error);
  // JTP-5: a booking the student pays through Ropida shows Pay only: no instructor payment
  // instructions and no screenshot step. The rest (a ₱0 session, Pay off) show today's details.
  const payBooked = booked.filter((r) => r.booking.payWithRopida);
  const todayBooked = booked.filter((r) => !r.booking.payWithRopida);
  const payOnly = payBooked.length > 0 && todayBooked.length === 0;
  const payTotal = groupPaymentBlocks(payBooked).reduce((sum, b) => sum + b.total, 0);
  // PA-5: a session may carry its own payment instructions, so the student gets one block per
  // destination, each with the amount that goes there.
  const paymentBlocks = groupPaymentBlocks(todayBooked);
  const hasPaymentDetails = paymentBlocks.some((b) => b.paymentInstructions || b.paymentQrUrl);
  const split = paymentBlocks.length > 1;
  // Comes from the booking response, not the public instructor payload — the address is only
  // released to students who actually booked. See transformInstructor.
  const instructorEmail = todayBooked[0]?.booking?.instructorEmail;

  // Pinned below the scrolling body, so the action stays reachable however long the instructor's
  // payment instructions run (TTT-83).
  const footer = step === 'review' ? (
    <div>
      {freeCancelHours != null && (
        <p data-testid="cancellation-rule" style={{ fontFamily: font, fontSize: type.label, color: T.walnut, lineHeight: 1.5, margin: '0 0 10px' }}>
          <strong>Cancellation:</strong> you can cancel free until {freeCancelHours} hours before the session. After that, contact your instructor to cancel.
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn variant="ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }} disabled={loading}>Cancel</Btn>
        <Btn onClick={handleConfirm} disabled={loading || list.length === 0} style={{ flex: 2, justifyContent: 'center' }}>
          {loading ? 'Sending…' : many ? `Request ${list.length} bookings →` : 'Request booking →'}
        </Btn>
      </div>
    </div>
  ) : (
    <Btn onClick={() => { onConfirm?.(); onClose(); }} style={{ width: '100%', justifyContent: 'center' }}>Go to my dashboard →</Btn>
  );

  return (
    <Modal onClose={loading ? undefined : onClose} maxWidth={580} footer={footer} labelledBy={titleId}>
      <div style={{ padding: '28px 32px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          {instructor?.photo && <img src={assetUrl(instructor.photo)} alt={instructor.displayName} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top' }} />}
          <div>
            <p id={titleId} style={{ fontFamily: font, fontWeight: 700, fontSize: 15, color: T.espresso, margin: 0 }}>
              {step === 'done' ? (failed.length ? 'Requests sent (some seats were gone)' : many ? 'Requests sent!' : 'Request sent!') : `Book with ${instructor?.displayName}`}
            </p>
            <p style={{ fontFamily: font, fontSize: type.label, color: T.driftwood, margin: 0 }}>
              {many ? `${list.length} sessions selected` : list[0]?.title}
            </p>
          </div>
        </div>

        {step === 'review' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: font, fontSize: type.label, color: T.driftwood }}>Times shown in your timezone:</span>
              <TimezoneSelect value={studentTz} onChange={setStudentTz}
                style={{ fontFamily: font, fontSize: type.label, color: T.espresso, padding: '6px 8px', borderRadius: 8, border: '1px solid ' + T.border, background: T.white, outline: 'none', maxWidth: '60%' }} />
            </div>

            <div style={{ background: T.ivory, borderRadius: 16, border: '1px solid ' + T.border, overflow: 'hidden', marginBottom: 14 }}>
              {list.map((s) => (
                <div key={s.id} style={{ padding: '12px 20px', borderBottom: '1px solid ' + T.border, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontFamily: font, fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title}</p>
                    <p style={{ fontFamily: font, fontSize: type.label, color: T.walnut, margin: 0 }}>{formatInTz(s.startAt, studentTz)} · {s.duration} min · {s.seatsLeft} seat{s.seatsLeft !== 1 ? 's' : ''} left</p>
                  </div>
                  <span style={{ fontFamily: font, fontWeight: 700, fontSize: type.body, color: T.espresso, whiteSpace: 'nowrap' }}>₱{(s.price ?? 0).toLocaleString('en-PH')}</span>
                </div>
              ))}
              <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.warmGrey }}>
                <span style={{ fontFamily: font, fontWeight: 700, fontSize: type.body, color: T.espresso }}>Total</span>
                <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.espresso }}>₱{total.toLocaleString('en-PH')}</span>
              </div>
            </div>

            <label style={{ fontFamily: font, fontSize: type.label, fontWeight: 600, color: T.walnut, display: 'block', marginBottom: 6 }}>
              Note to instructor (optional)
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={2000}
                placeholder="Anything your instructor should know?"
                style={{ width: '100%', marginTop: 6, fontFamily: font, fontSize: type.body, color: T.espresso, padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, background: T.white, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </label>

            <p style={{ fontFamily: font, fontSize: type.label, color: T.driftwood, lineHeight: 1.6, margin: '10px 0 16px' }}>
              {payAvailable
                ? <>Right after this request, press Pay to pay on JTPay&apos;s pay page. Your seat{many ? 's are' : ' is'} confirmed once JTPay reports your payment Paid.</>
                : <>Payment is arranged directly with your instructor (GCash / bank transfer). You&apos;ll get the payment details right after this request, and {instructor?.displayName} confirms your seat{many ? 's' : ''} once payment is received.</>}
            </p>

          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '4px 0' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: failed.length ? T.caramelGold : T.terracotta, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ color: T.white, fontSize: type.h2, fontWeight: 700 }}>{failed.length ? '!' : '✓'}</span>
            </div>
            <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 8px' }}>
              {booked.length ? `Booking${booked.length > 1 ? 's' : ''} requested` : 'No seats booked'}
            </h3>
            <p style={{ fontFamily: font, fontSize: type.body, color: T.walnut, lineHeight: 1.7, margin: '0 0 16px' }}>
              {booked.length > 0 && payOnly && <>Your seat{booked.length > 1 ? 's are' : ' is'} reserved and <strong>awaiting payment</strong>. Press Pay to pay on JTPay&apos;s pay page.</>}
              {booked.length > 0 && !payOnly && <>Your seat{booked.length > 1 ? 's are' : ' is'} reserved and <strong>awaiting payment confirmation</strong>. Send your payment now.</>}
              {booked.length === 0 && 'None of the selected seats could be booked — see the details below.'}
            </p>

            {/* Per-session outcome */}
            <div style={{ background: T.ivory, borderRadius: 14, border: '1px solid ' + T.border, padding: '12px 16px', textAlign: 'left', marginBottom: 16 }}>
              {results.map((r) => (
                <div key={r.session.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: font, fontSize: type.body, color: T.espresso, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.session.title} · {formatInTz(r.session.startAt, studentTz, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                  <span style={{ fontFamily: font, fontWeight: 700, fontSize: type.label, color: r.booking ? T.sageGreen : T.errorRed, whiteSpace: 'nowrap' }}>
                    {r.booking ? '✓ requested' : r.error}
                  </span>
                </div>
              ))}
            </div>

            {payBooked.length > 0 && (
              <div data-testid="pay-block" style={{ background: T.ivory, borderRadius: 14, border: '1px solid ' + T.border, padding: '16px 20px', textAlign: 'left', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, margin: '0 0 8px' }}>
                  <p style={{ fontFamily: font, fontWeight: 700, fontSize: type.body, color: T.espresso, margin: 0 }}>Pay for your booking{payBooked.length > 1 ? 's' : ''}</p>
                  <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, whiteSpace: 'nowrap' }}>₱{payTotal.toLocaleString('en-PH')}</span>
                </div>
                <p style={{ fontFamily: font, fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: '0 0 12px' }}>
                  Press Pay to pay on JTPay&apos;s pay page. You need no screenshot: your booking is confirmed once JTPay reports your payment Paid.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <PayButton booking={{ id: payBooked[0].booking.id, status: 'pending', payWithRopida: true, bookingPayment: null }} />
                </div>
              </div>
            )}

            {todayBooked.length > 0 && (hasPaymentDetails ? (
              <div style={{ marginBottom: 20 }}>
                {split && (
                  <p style={{ fontFamily: font, fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: '0 0 10px', textAlign: 'left' }}>
                    Some sessions have their own payment details. Send each amount to the details shown with it.
                  </p>
                )}
                {paymentBlocks.map((block) => (
                  <div key={block.sessions.map((s) => s.id).join('|')} data-testid="payment-block"
                    style={{ background: T.ivory, borderRadius: 14, border: '1px solid ' + T.border, padding: '16px 20px', textAlign: 'left', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, margin: '0 0 8px' }}>
                      <p style={{ fontFamily: font, fontWeight: 700, fontSize: type.body, color: T.espresso, margin: 0 }}>How to pay</p>
                      <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, whiteSpace: 'nowrap' }}>₱{block.total.toLocaleString('en-PH')}</span>
                    </div>
                    {split && (
                      <p style={{ fontFamily: font, fontSize: type.label, color: T.driftwood, lineHeight: 1.5, margin: '0 0 8px' }}>
                        For {block.sessions.map((s) => s.title).join(', ')}
                      </p>
                    )}
                    {block.paymentInstructions && (
                      <p style={{ fontFamily: font, fontSize: type.body, color: T.walnut, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{block.paymentInstructions}</p>
                    )}
                    {!block.paymentInstructions && !block.paymentQrUrl && (
                      <p style={{ fontFamily: font, fontSize: type.body, color: T.driftwood, margin: 0 }}>
                        Your instructor will send payment details for {block.sessions.length > 1 ? 'these sessions' : 'this session'} — check your email or dashboard.
                      </p>
                    )}
                    {/* Centred in the how-to-pay box: an <img> is inline, so it used to hug the left
                        edge of this left-aligned block (TTT-83). Wrapped in a lightbox so the QR
                        can be zoomed — thumbnail-sized, it's too small to scan (TTT-84). */}
                    {block.paymentQrUrl && (
                      <ImageLightbox src={assetUrl(block.paymentQrUrl)} alt="Payment QR code"
                        thumbStyle={{ maxWidth: 200, width: '100%', borderRadius: 10, border: '1px solid ' + T.border, display: 'block', margin: '10px auto 0' }} />
                    )}
                  </div>
                ))}
                {/* One instructor per pop-up, so the proof-of-payment address shows once. */}
                <ProofOfPaymentNote email={instructorEmail} style={{ margin: 0 }} />
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: font, fontSize: type.body, color: T.driftwood, margin: 0, textAlign: 'left' }}>
                  Your instructor will send payment details — check your email or dashboard.
                </p>
                <ProofOfPaymentNote email={instructorEmail} />
              </div>
            ))}

            {booked.length > 0 && (
              <p style={{ fontFamily: font, fontSize: type.label, color: T.driftwood, lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
                {payOnly
                  ? 'Once JTPay reports your payment Paid, you receive a confirmation email with the session link.'
                  : 'Once settled, please check your email. You will receive a confirmation email with the session link.'}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export { BookingModal };
